import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../Layout";
import { sortsDatas } from "../../components/Datas";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { BiChevronDown, BiPlus, BiTime, BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { BsCalendarMonth } from "react-icons/bs";
import { MdFilterList, MdOutlineCalendarMonth } from "react-icons/md";
import { toast } from "react-hot-toast";
import { Button, FromToDate, Select } from "../../components/Form";
import { PatientTable } from "../../components/Tables";
import axios from "axios";
import { BaseUrl } from "../../BaseUrl";

function Patients() {
  const [status, setStatus] = useState(sortsDatas.filterPatient[0]);
  const [gender, setGender] = useState(sortsDatas.genderFilter[0]);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;
  const navigate = useNavigate();
  const location = useLocation();
  
  // Patient data state
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const itemsPerPage = 4;
  
  // Stats state
  const [stats, setStats] = useState({
    todayTotal: 0,
    monthTotal: 0,
    yearTotal: 0,
  });

  // Fetch patient stats
  const fetchPatientStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BaseUrl}/doctor/patients/stats`, {
        withCredentials: true,
      });
      
      if (response.data) {
        setStats({
          todayTotal: response.data.todayTotal || 0,
          monthTotal: response.data.monthTotal || 0,
          yearTotal: response.data.yearTotal || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching patient stats:", error);
    }
  }, []);

  // Fetch patients with filters
  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);
      
      if (searchQuery.trim()) {
        params.append("search", searchQuery.trim());
      }
      
      if (gender?.name && gender.name !== "All") {
        params.append("gender", gender.name);
      }
      
      if (status?.name && status.name !== "Sort by...") {
        params.append("sortBy", status.name === "Newest" ? "newest" : "oldest");
      }
      
      if (startDate && endDate) {
        params.append("fromDate", startDate.toISOString().split('T')[0]);
        params.append("toDate", endDate.toISOString().split('T')[0]);
      }
      
      const response = await axios.get(
        `${BaseUrl}/doctor/patients/filter?${params.toString()}`,
        {
          withCredentials: true,
        }
      );
      
      console.log("Patients API Response:", response.data);
      
      // Handle both paginated and non-paginated responses
      const responseData = response.data?.data || response.data;
      const paginationInfo = response.data?.pagination;
      
      console.log("Processed Response Data:", responseData);
      
      if (responseData && Array.isArray(responseData)) {
        // Transform data to match PatientTable format
        const transformedData = responseData.map((patient, index) => {
          const fullName = patient.title || patient.fullName || "Unknown";
          const nameParts = fullName.split(" ");
          const firstName = nameParts[0] || "";
          const lastName = nameParts[1] || "";
          const avatarUrl = patient.image || 
            `https://api.dicebear.com/5.x/initials/svg?seed=${firstName}_${lastName}`;
          
          return {
            id: patient._id || index + 1,
            _id: patient._id,
            title: fullName, // Main title for display
            user: {
              image: avatarUrl,
              title: fullName,
              phone: patient.phone || "N/A",
              email: patient.email || "N/A",
            },
            date: patient.date || patient.createdAt ? 
              new Date(patient.date || patient.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              }) : "N/A",
            gender: patient.gender || "N/A",
            blood: patient.blood || "N/A",
            age: patient.age || "N/A",
            createdAt: patient.createdAt,
          };
        });
        
        setPatients(transformedData);
        
        // Use pagination info if available, otherwise calculate from data
        if (paginationInfo) {
          setTotalPatients(paginationInfo.totalPatients || transformedData.length);
          setTotalPages(paginationInfo.totalPages || 1);
        } else {
          setTotalPatients(transformedData.length);
          setTotalPages(Math.ceil(transformedData.length / itemsPerPage) || 1);
        }
      } else {
        setPatients([]);
        setTotalPatients(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching patients:", error);
      toast.error(error.response?.data?.error || "Error loading patients");
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, gender, status, startDate, endDate, itemsPerPage]);

  useEffect(() => {
    fetchPatientStats();
  }, [fetchPatientStats]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Refresh data when navigating back to this page
  useEffect(() => {
    if (location.pathname === '/patients') {
      fetchPatients();
      fetchPatientStats();
    }
  }, [location.pathname]);

  const sorts = [
    {
      id: 2,
      selected: status,
      setSelected: setStatus,
      datas: sortsDatas.filterPatient,
    },
    {
      id: 3,
      selected: gender,
      setSelected: setGender,
      datas: sortsDatas.genderFilter,
    },
  ];
  
  // Stats boxes with real data
  const boxes = [
    {
      id: 1,
      title: "Today Patients",
      value: stats.todayTotal.toString(),
      color: ["bg-[#0095D9]", "text-[#0095D9]"],
      icon: BiTime,
    },
    {
      id: 2,
      title: "Monthly Patients",
      value: stats.monthTotal.toString(),
      color: ["bg-[#EF356C]", "text-[#EF356C]"],
      icon: BsCalendarMonth,
    },
    {
      id: 3,
      title: "Yearly Patients",
      value: stats.yearTotal.toString(),
      color: ["bg-[#2CDB00]", "text-[#2CDB00]"],
      icon: MdOutlineCalendarMonth,
    },
  ];

  // Preview function
  const previewPayment = (id) => {
    navigate(`/patients/preview/${id}`);
  };

  // Handle filter button
  const handleFilter = () => {
    setCurrentPage(1); // Reset to first page when filtering
    fetchPatients();
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Filter patients based on search (client-side for immediate feedback)
  const filteredPatients = patients.filter((patient) => {
    if (!searchQuery.trim()) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      patient.user.title.toLowerCase().includes(searchLower) ||
      patient.user.email.toLowerCase().includes(searchLower) ||
      patient.user.phone.toLowerCase().includes(searchLower)
    );
  });

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalPatients || filteredPatients.length);

  return (
    <Layout>
      {/* add button */}
      <Link
        to='/patients/create'
        className='flex-colo button-fb fixed bottom-8 right-12 z-50 h-16 w-16 animate-bounce rounded-full border border-border bg-[#0095D9] text-white'
      >
        <BiPlus className='text-2xl' />
      </Link>

      <h1 className='text-xl font-semibold'>Patients</h1>
      {/* boxes */}
      <div className='mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3'>
        {boxes.map((box) => (
          <div
            key={box.id}
            className='flex-btn gap-4 rounded-xl border-[1px] border-border bg-white p-5'
          >
            <div className='w-3/4'>
              <h2 className='text-sm font-medium'>{box.title}</h2>
              <h2 className='my-6 text-xl font-medium'>{box.value}</h2>
              <p className='text-xs text-textGray'>
                Total Patients <span className={box.color[1]}>{box.value}</span>{" "}
                {box.title === "Today Patients"
                  ? "today"
                  : box.title === "Monthly Patients"
                    ? "this month"
                    : "this year"}
              </p>
            </div>
            <div
              className={`flex-colo text-md h-10 w-10 rounded-full text-white ${box.color[0]}`}
            >
              <box.icon />
            </div>
          </div>
        ))}
      </div>
      {/* datas */}
      <div
        // data-aos='fade-up'
        // data-aos-duration='1000'
        // data-aos-delay='10'
        // data-aos-offset='200'
        className='my-8 rounded-xl border-[1px] border-border bg-white p-5'
      >
        <div className='grid grid-cols-1 gap-8 xs:grid-cols-3 md:grid-cols-4 lg:grid-cols-4'>
          {/* sort  */}
          {sorts.map((item) => (
            <Select
              key={item.id}
              selectedPerson={item.selected}
              setSelectedPerson={item.setSelected}
              datas={item.datas}
            >
              <div className='flex h-14 w-full items-center justify-between rounded-md border border-border bg-dry px-4 text-xs text-main'>
                <p>{item.selected.name}</p>
                <BiChevronDown className='text-xl' />
              </div>
            </Select>
          ))}
          {/* date */}
          <FromToDate
            startDate={startDate}
            endDate={endDate}
            bg='bg-dry'
            onChange={(update) => setDateRange(update)}
          />
          {/* Filter button */}
          <Button
            label='Filter'
            Icon={MdFilterList}
            onClick={handleFilter}
          />
        </div>
        <input
          type='text'
          placeholder='Search "Patients"'
          className='mt-6 h-14 w-full rounded-md border border-border bg-dry px-4 text-sm text-main md:w-[50%]'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setCurrentPage(1);
              fetchPatients();
            }
          }}
        />
        <div className='mt-8 w-full overflow-x-scroll'>
          {loading ? (
            <div className='flex items-center justify-center py-10'>
              <p className='text-textGray'>Loading patients...</p>
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className='flex items-center justify-center py-10'>
              <p className='text-textGray'>
                {searchQuery
                  ? "No patients found matching your search."
                  : "No patients found."}
              </p>
            </div>
          ) : (
            <>
              <PatientTable
                data={filteredPatients}
                functions={{
                  preview: previewPayment,
                }}
                used={false}
              />
              
              {/* Pagination */}
              <div className='mt-6 flex items-center justify-between'>
                <p className='text-sm text-textGray'>
                  Showing {startIndex}-{endIndex} of {totalPatients || filteredPatients.length} patients
                </p>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center justify-center h-10 w-10 rounded-md border ${
                      currentPage === 1
                        ? "border-gray-300 text-gray-300 cursor-not-allowed"
                        : "border-border text-main hover:bg-dry"
                    }`}
                  >
                    <BiChevronLeft className='text-xl' />
                  </button>
                  <span className='px-4 py-2 text-sm font-medium text-main bg-subMain bg-opacity-10 rounded-md'>
                    {currentPage}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className={`flex items-center justify-center h-10 w-10 rounded-md border ${
                      currentPage >= totalPages
                        ? "border-gray-300 text-gray-300 cursor-not-allowed"
                        : "border-border text-main hover:bg-dry"
                    }`}
                  >
                    <BiChevronRight className='text-xl' />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Patients;
